import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { logger } from '../utils/logger';

// Import all translation files
import en from '../locales/en.json';
import es from '../locales/es.json';
import fr from '../locales/fr.json';
import de from '../locales/de.json';
import ja from '../locales/ja.json';

export type SupportedLanguage = 'en' | 'es' | 'fr' | 'de' | 'ja';

export const SUPPORTED_LANGUAGES: Record<SupportedLanguage, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  ja: '日本語'
};

class LanguageService {
  private static instance: LanguageService;
  private currentLanguage: SupportedLanguage = 'en';

  private constructor() {
    this.initializeI18n();
  }

  public static getInstance(): LanguageService {
    if (!LanguageService.instance) {
      LanguageService.instance = new LanguageService();
    }
    return LanguageService.instance;
  }

  private async initializeI18n() {
    try {
      await i18n
        .use(initReactI18next)
        .init({
          resources: {
            en: { translation: en },
            es: { translation: es },
            fr: { translation: fr },
            de: { translation: de },
            ja: { translation: ja }
          },
          lng: this.currentLanguage,
          fallbackLng: 'en',
          interpolation: {
            escapeValue: false
          },
          react: {
            useSuspense: true
          }
        });

      logger.info(`Language system initialized with language: ${this.currentLanguage}`);
    } catch (error) {
      logger.error('Failed to initialize language system:', error);
    }
  }

  public async changeLanguage(language: SupportedLanguage): Promise<void> {
    try {
      await i18n.changeLanguage(language);
      this.currentLanguage = language;
      logger.info(`Language changed to: ${language}`);
    } catch (error) {
      logger.error(`Failed to change language to ${language}:`, error);
      throw new Error(`Failed to change language to ${language}`);
    }
  }

  public getCurrentLanguage(): SupportedLanguage {
    return this.currentLanguage;
  }

  public getSupportedLanguages(): typeof SUPPORTED_LANGUAGES {
    return SUPPORTED_LANGUAGES;
  }

  public formatNumber(number: number, options?: Intl.NumberFormatOptions): string {
    return new Intl.NumberFormat(this.currentLanguage, options).format(number);
  }

  public formatDate(date: Date, options?: Intl.DateTimeFormatOptions): string {
    return new Intl.DateTimeFormat(this.currentLanguage, options).format(date);
  }

  getLanguageName(code: SupportedLanguage): string {
    return SUPPORTED_LANGUAGES[code] || code;
  }

  isRTL(language: SupportedLanguage): boolean {
    // Add RTL languages here if needed
    const rtlLanguages: SupportedLanguage[] = [];
    return rtlLanguages.includes(language);
  }

  formatTime(date: Date): string {
    return new Intl.DateTimeFormat(this.currentLanguage, {
      hour: 'numeric',
      minute: 'numeric',
    }).format(date);
  }

  formatRelativeTime(value: number, unit: Intl.RelativeTimeFormatUnit): string {
    const rtf = new Intl.RelativeTimeFormat(this.currentLanguage, {
      numeric: 'auto',
    });
    return rtf.format(value, unit);
  }
}

export const languageService = LanguageService.getInstance();
export default languageService; 